import React, { createContext, useState, useEffect, useRef, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import firebase from "firebase/compat/app"
import "firebase/compat/auth"
import "firebase/compat/firestore"
// Use Firebase Authentication instead of backend auth
import { auth } from "../config/index";
import { saveUser, getUser } from "../config/firebaseService";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateEmail as firebaseUpdateEmail,
  updatePassword as firebaseUpdatePassword,
    updateProfile,
  getIdToken,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

// Create an authentication context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // Holds the logged-in user data
    const [loading, setLoading] = useState(true); // Tracks loading state
    const logoutTimerRef = useRef(null);

    // Handles user logout (memoized)
    const logout = useCallback(async () => {
        // capture uid before sign-out (auth.currentUser becomes null after)
        let uid = null;
        try {
            uid = auth.currentUser?.uid || null;
            if (!uid) {
                const cached = localStorage.getItem("user");
                if (cached) {
                    try { uid = JSON.parse(cached)?.id || null; } catch { /* ignore */ }
                }
            }
        } catch { /* ignore */ }

        try {
            await signOut(auth);
            setUser(null);
            localStorage.removeItem("user");
            localStorage.removeItem("token");
            console.log("User logged out successfully.");

            // Log logout action to backend (best-effort)
            try {
                await fetch("http://localhost:8000/add_log", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ action: "User logged out", user_id: uid }),
                });
            } catch (err) {
                console.warn("Failed to send logout log to backend:", err);
            }
        } catch (error) {
            console.error("Logout error:", error);
        }
    }, []);

    useEffect(() => {
        // Use Firebase onAuthStateChanged to keep user in sync
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
                // You can fetch additional profile fields if you store them elsewhere
                const token = await getIdToken(firebaseUser);
        const providerIds = (firebaseUser.providerData || []).map((p) => p?.providerId).filter(Boolean);
        const canChangePassword = providerIds.includes("password");
                // Prefer Firestore profile name, fallback to displayName/email
                let profileName = firebaseUser.displayName || "";
                try {
                    const profile = await getUser(firebaseUser.uid);
                    if (profile?.name) profileName = profile.name;
                } catch { /* ignore */ }
                                const userData = {
                    id: firebaseUser.uid,
                    email: firebaseUser.email,
                                        firstName: "",
                                        lastName: "",
                        name: profileName || firebaseUser.email || "",
                    role: "User",
                    token,
            authProviders: providerIds,
            canChangePassword,
                };
                setUser(userData);
                localStorage.setItem("user", JSON.stringify(userData));
                localStorage.setItem("token", token);
                console.log("🔹 User loaded from Firebase Auth:", userData);
            } else {
                setUser(null);
                localStorage.removeItem("user");
                localStorage.removeItem("token");
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Handles user login using Firebase
    const login = async (email, password) => {
        try {
            const credential = await signInWithEmailAndPassword(auth, email, password);
            const firebaseUser = credential.user;
            const token = await getIdToken(firebaseUser);
            const providerIds = (firebaseUser.providerData || []).map((p) => p?.providerId).filter(Boolean);
                        // Prefer Firestore profile name
                        let profileName = firebaseUser.displayName || "";
                        try {
                            const profile = await getUser(firebaseUser.uid);
                            if (profile?.name) profileName = profile.name;
                        } catch { /* ignore */ }

            const userData = {
                id: firebaseUser.uid,
                email: firebaseUser.email,
                                firstName: "",
                                lastName: "",
                                name: profileName || firebaseUser.email || "",
                role: "User",
                authProviders: providerIds,
                canChangePassword: providerIds.includes("password"),
            };

            setUser(userData);
            localStorage.setItem("user", JSON.stringify(userData));
            localStorage.setItem("token", token);

            // Ensure user document exists/updated
            try {
                await saveUser(firebaseUser.uid, firebaseUser.displayName || "", firebaseUser.email || "");
            } catch (e) {
                console.warn("Failed to upsert user in Firestore on login:", e);
            }

            // Optionally inform backend for logging
            try {
                await fetch("/add_log", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "User logged in", user_id: userData.id }),
});

            } catch (err) {
                console.warn("Failed to send login log to backend:", err);
            }

            return { success: true };
        } catch (error) {
            console.error("Login error:", error);
            return { success: false, message: error.message || "Firebase login error" };
        }
    };

    // Sign in with Google (works for new or existing users)
    const signInWithGoogle = async () => {
        try {
            const provider = new GoogleAuthProvider();
            const credential = await signInWithPopup(auth, provider);
            const firebaseUser = credential.user;
            const token = await getIdToken(firebaseUser);
            const providerIds = (firebaseUser.providerData || []).map((p) => p?.providerId).filter(Boolean);
                        let profileName = firebaseUser.displayName || firebaseUser.email || "";
                        try {
                            const profile = await getUser(firebaseUser.uid);
                            if (profile?.name) profileName = profile.name;
                        } catch { /* ignore */ }

            const userData = {
                id: firebaseUser.uid,
                email: firebaseUser.email,
                                name: profileName,
                role: "User",
                authProviders: providerIds,
                canChangePassword: providerIds.includes("password"),
            };

            setUser(userData);
            localStorage.setItem("user", JSON.stringify(userData));
            localStorage.setItem("token", token);

            // Ensure user document exists/updated
            try {
                await saveUser(firebaseUser.uid, userData.name || "", firebaseUser.email || "");
            } catch (e) {
                console.warn("Failed to upsert user in Firestore on Google sign-in:", e);
            }

            // Log the event (best-effort)
            try {
                await fetch("/add_log", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "User logged in (google)", user_id: userData.id }),
});

            } catch (err) {
                console.warn("Failed to send google login log to backend:", err);
            }

            return { success: true, user_id: userData.id, name: userData.name, email: userData.email };
        } catch (error) {
            console.error("Google sign-in error:", error);
            return { success: false, message: error.message || "Google sign-in error" };
        }
    };

    // const navigate = useNavigate();

    // Resets logout timer on user activity
    const resetLogoutTimer = useCallback(() => {
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = setTimeout(() => {
            logout();
        }, 60 * 60 * 1000);  // 60 minutes
    }, [logout]);

    // Auto logout after inactivity
    useEffect(() => {
        if (user) {
            resetLogoutTimer(); // start timer when user logs in or user changes

            // user activity events
            window.addEventListener("mousemove", resetLogoutTimer);
            window.addEventListener("keydown", resetLogoutTimer);
            window.addEventListener("click", resetLogoutTimer);
            window.addEventListener("focus", resetLogoutTimer);
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible") {
                    resetLogoutTimer();
                }
            });
        }

        return () => {
            if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
            window.removeEventListener("mousemove", resetLogoutTimer);
            window.removeEventListener("keydown", resetLogoutTimer);
            window.removeEventListener("click", resetLogoutTimer);
            window.removeEventListener("focus", resetLogoutTimer);
            // visibilitychange listener used an inline callback; safe to ignore on cleanup
        };
    }, [user, resetLogoutTimer]);

     // Sign up using Firebase
     const signup = async (vorname, nachname, email, password) => {
        try {
            const credential = await createUserWithEmailAndPassword(auth, email, password);
            const firebaseUser = credential.user;

                        // Set displayName to combined name
                        const displayName = [vorname, nachname].filter(Boolean).join(" ");
                        try {
                            await updateProfile(firebaseUser, { displayName });
                        } catch { /* ignore */ }

            const providerIds = (firebaseUser.providerData || []).map((p) => p?.providerId).filter(Boolean);
            const userData = {
                id: firebaseUser.uid,
                email: firebaseUser.email,
                firstName: vorname || "",
                lastName: nachname || "",
                                name: displayName.trim(),
                role: "User",
                authProviders: providerIds,
                canChangePassword: providerIds.includes("password"),
            };

            setUser(userData);
            localStorage.setItem("user", JSON.stringify(userData));

            // Create user doc on signup (use combined name)
            try {
                await saveUser(firebaseUser.uid, displayName, firebaseUser.email || "");
            } catch (e) {
                console.warn("Failed to create user doc in Firestore on signup:", e);
            }

            // Inform backend for logging or user record (best-effort)
            try {
                await fetch("/add_log", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "User signed up", user_id: userData.id }),
});

            } catch (err) {
                console.warn("Failed to send signup log to backend:", err);
            }

            return { success: true, user_id: userData.id };
        } catch (error) {
            console.error("Signup error:", error);
            return { success: false, message: error.message || "Firebase signup error" };
        }
     };

     // Update user email
     const updateEmail = async (newEmail, currentPassword) => {
        if (!user) {
            return { success: false, message: "User not logged in" };
        }

        try {
            const currentUser = auth.currentUser;
            await firebaseUpdateEmail(currentUser, newEmail);
            // Update local user data
            const updatedUser = { ...user, email: newEmail };
            setUser(updatedUser);
            localStorage.setItem("user", JSON.stringify(updatedUser));

            return { success: true, message: "Email updated" };
        } catch (error) {
            console.error("Error updating email:", error);
            return { success: false, message: error.message || "Server error" };
        }
    };

     //update password
     const updatePassword = async (currentPassword, newPassword) => {
        if (!user) {
            return { success: false, message: "User not logged in" };
        }

        try {
            const currentUser = auth.currentUser;
            const providerIds = (currentUser?.providerData || []).map((p) => p?.providerId).filter(Boolean);
            if (!providerIds.includes("password")) {
                return { success: false, message: "Password can't be changed for Google accounts here." };
            }
            await firebaseUpdatePassword(currentUser, newPassword);
            return { success: true, message: "Password updated" };
        } catch (error) {
            console.error("Error updating password:", error);
            return { success: false, message: error.message || "Server error" };
        }
    };

    return (
    <AuthContext.Provider value={{
        user,
        login,
        logout,
        loading,
        updateEmail,
        updatePassword,
        signup,
        signInWithGoogle,
        resetLogoutTimer,
        canChangePassword: !!(user?.authProviders || []).includes?.("password") || !!user?.canChangePassword,
    }}>
            {children}
        </AuthContext.Provider>
    );
};
